<!--
  The station clock.

  V1 had this and it is one of the most recognisably ShioCast things in the panel: a neon
  seven-segment readout in the header, the accent blue on near-black, with hundredths
  ticking. It
  is kept because it is the identity, and because a broadcaster genuinely does look at the
  clock constantly.

  Two things make it a broadcast clock rather than decoration. It reads in the station's
  timezone, never the viewer's, so an operator abroad sees the time the station is on. And
  it carries a connection dot, because a clock that has silently stopped updating is worse
  than no clock: it looks authoritative and is wrong.
-->
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { getStationTimeZone } from '../clock.js';
import { ICONS } from '../icons.js';

const props = withDefaults(
  defineProps<{
    timezone?: string;
    /** False when the panel cannot reach the API, which dims the readout. */
    connected?: boolean;
    /** Hundredths are the V1 look. Off for a reduced-motion or low-power context. */
    showHundredths?: boolean;
    /** Spoken prefix for the readout, translated by the caller. */
    label?: string;
  }>(),
  { connected: true, showHundredths: true, label: 'Station time' },
);

const now = ref(new Date());
let frame: number | undefined;
let timer: ReturnType<typeof setInterval> | undefined;

const zone = computed(() => props.timezone ?? getStationTimeZone());

const parts = computed(() => {
  // Digits only, so the locale is fixed: a 24-hour readout should not become 12-hour
  // because somebody switched the interface language.
  const formatted = new Intl.DateTimeFormat('nl-NL', {
    timeZone: zone.value,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(now.value);

  const [hour = '00', minute = '00', second = '00'] = formatted.split(':');
  return {
    hour,
    minute,
    second,
    hundredths: String(Math.floor(now.value.getMilliseconds() / 10)).padStart(2, '0'),
  };
});

/** The full time, for anyone who cannot read the segmented display. */
const spoken = computed(
  () => `${props.label} ${parts.value.hour}:${parts.value.minute}:${parts.value.second}`,
);

function tick(): void {
  now.value = new Date();
  frame = requestAnimationFrame(tick);
}

onMounted(() => {
  // Hundredths need a frame loop; seconds do not, and a timer is far cheaper. Respecting
  // reduced motion here is not cosmetic: a readout flickering at 100 Hz is exactly the
  // kind of thing the preference exists for.
  const reduced =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (props.showHundredths && !reduced) {
    frame = requestAnimationFrame(tick);
  } else {
    timer = setInterval(() => {
      now.value = new Date();
    }, 1000);
  }
});

onUnmounted(() => {
  if (frame !== undefined) cancelAnimationFrame(frame);
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div class="shio-clock" :class="{ 'shio-clock--offline': !connected }" role="timer">
    <component
      :is="connected ? ICONS.Wifi : ICONS.WifiOff"
      class="shio-clock__signal"
      :size="14"
      aria-hidden="true"
    />
    <span class="shio-clock__readout" :aria-label="spoken">
      <span aria-hidden="true">{{ parts.hour }}</span>
      <span class="shio-clock__colon" aria-hidden="true">:</span>
      <span aria-hidden="true">{{ parts.minute }}</span>
      <span class="shio-clock__colon" aria-hidden="true">:</span>
      <span aria-hidden="true">{{ parts.second }}</span>
      <span v-if="showHundredths" class="shio-clock__fraction" aria-hidden="true">
        .{{ parts.hundredths }}
      </span>
    </span>
  </div>
</template>

<style scoped>
.shio-clock {
  display: inline-flex;
  align-items: center;
  gap: var(--shio-space-2);
  padding: var(--shio-space-1) var(--shio-space-3);
  border: 1px solid hsl(var(--shio-accent-primary) / 0.45);
  border-radius: var(--shio-radius-md);
  background: hsl(var(--shio-surface-base));
  box-shadow:
    inset 0 0 18px hsl(var(--shio-accent-primary) / 0.12),
    0 0 12px hsl(var(--shio-accent-primary) / 0.15);
}

.shio-clock__signal {
  color: hsl(var(--shio-accent-primary));
}

.shio-clock__readout {
  font-family: var(--shio-font-display);
  font-size: var(--shio-type-timecode-size);
  font-weight: 700;
  letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
  color: hsl(var(--shio-accent-primary));
  text-shadow:
    0 0 6px hsl(var(--shio-accent-primary) / 0.7),
    0 0 18px hsl(var(--shio-accent-primary) / 0.35);
}

.shio-clock__colon {
  opacity: 0.75;
}

/* Hundredths are deliberately smaller and dimmer: they are texture, not information. */
.shio-clock__fraction {
  font-size: var(--shio-type-body-sm-size);
  opacity: 0.65;
}

/* A clock that is not updating must not look authoritative. */
.shio-clock--offline {
  border-color: hsl(var(--shio-border));
  box-shadow: none;
}

.shio-clock--offline .shio-clock__signal,
.shio-clock--offline .shio-clock__readout {
  color: hsl(var(--shio-text-muted));
  text-shadow: none;
}

@media (max-width: 720px) {
  .shio-clock__readout {
    font-size: var(--shio-type-section-size);
  }

  .shio-clock__fraction {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .shio-clock__fraction {
    display: none;
  }
}
</style>
